import { ReactNode, useMemo } from 'react';
import { Spinner, Tabs } from '@chakra-ui/react';
import { Text, Button, Icon, Colors } from '@mercator-gcp/grout';
import { TabsList, TabLabel } from 'components/elements/TabsList';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { useParcelProjectTabCounts } from 'hooks/projectMap/useParcelProjectTabCounts';
import RenderResults from 'pages/ProjectInsights/ProjectLists/SearchResults/RenderResults';
import {
  useSelectedPolygonBoundsOnMap,
  useSelectedPolygonId,
  useShowAllProjectsWithinPolygon,
} from 'state/map';
import { formatInteger } from 'utils/format';

const ParcelTabSpinner = () => (
  <Spinner
    thickness="2px"
    speed="0.65s"
    emptyColor="gray.200"
    color="#027a3b"
    boxSize="14px"
    display="inline-block"
    verticalAlign="middle"
    ml="4px"
    flexShrink={0}
  />
);

function parcelTabCountSuffix(
  isError: boolean,
  isLoading: boolean,
  count: number | undefined,
): ReactNode {
  if (isError) {
    return ' (—)';
  }
  if (isLoading && count === undefined) {
    return <ParcelTabSpinner />;
  }

  return ` (${formatInteger(count ?? 0)})`;
}

const listStyle = {
  height: '100%',
  paddingTop: 'padding_8',
  rowGap: 'padding_8',
  paddingLeft: 'padding_8',
  paddingRight: 'padding_8',
};

const ProjectListComponent = () => {
  const [selectedPolygonBoundsOnMap, setSelectedPolygonBoundsOnMap] =
    useSelectedPolygonBoundsOnMap();
  const [, setSelectedPolygon] = useSelectedPolygonId();
  const [showAllProjectsWithinPolygon, setShowAllProjectsWithinPolygon] =
    useShowAllProjectsWithinPolygon();

  const {
    matchFiltersCount,
    matchFiltersLoading,
    matchFiltersError,
    allInParcelCount,
    allInParcelLoading,
    allInParcelError,
  } = useParcelProjectTabCounts();

  const parcelTabLabels = useMemo((): TabLabel[] => {
    return [
      {
        label: `Filtered ${parcelTabCountSuffix(
          matchFiltersError,
          matchFiltersLoading,
          matchFiltersCount,
        )}`,
      },
      {
        label: `All ${parcelTabCountSuffix(
          allInParcelError,
          allInParcelLoading,
          allInParcelCount,
        )}`,
      },
    ];
  }, [
    matchFiltersCount,
    matchFiltersLoading,
    matchFiltersError,
    allInParcelCount,
    allInParcelLoading,
    allInParcelError,
  ]);

  const clearSelectedPolygonData = () => {
    setSelectedPolygonBoundsOnMap('');
    setSelectedPolygon('');
  };

  return (
    <FlexColumn sx={listStyle}>
      {selectedPolygonBoundsOnMap && (
        <FlexRow>
          <Tabs
            variant="enclosed"
            index={showAllProjectsWithinPolygon ? 1 : 0}
            onChange={() =>
              setShowAllProjectsWithinPolygon(!showAllProjectsWithinPolygon)
            }
          >
            <FlexRow>
              <TabsList tabLabels={parcelTabLabels} />
            </FlexRow>
          </Tabs>
          <Button
            variant="unstyled"
            startAdornment={<Icon fill={Colors.Tangerine900} name={Icon.Name.CloseX} />}
            onClick={clearSelectedPolygonData}
          >
            <Text color={Colors.Tangerine900}>Clear parcel</Text>
          </Button>
        </FlexRow>
      )}
      <RenderResults />
    </FlexColumn>
  );
};

export default ProjectListComponent;
